import { asError } from '@/util/errorUtil'
import '@/polyfill'
import '../styles/index.scss'
import { checkInviteGate } from '@/auth/inviteGate'
import { InviteRequiredWall } from '@/auth/InviteRequiredWall'
import { initializePlaylistPlayerBindings } from '@/playback/playerPlaylistBinding'
import { initializeDynamicMessagingLazy } from '@/messaging/dm/dmBootstrap'
import { initializeMediaSessionBindings } from '@/transport/mediaSessionBinding'
import { setI18nConfig } from '@amzn/amp-i18n'
import {
    initializeDisplayLocale,
    installDisplayLanguageSync,
} from '@/displayLanguage/displayLanguageService'
import { cblAuthConfig, stratusConfig } from '@amzn/vinyl-amzn/cbl'
import { getApiHost, proxiedHost } from '@/service/useLiveHost'
import { initializeAppView } from '@/AppView'
import { initializeConnectedObserver } from '@amazon/vinyl-tsx'
import { hideMainLoader } from '@/hideMainLoader'
import { initializeDeviceInfo } from '@/device/device'
import { initializeOfflineCache } from '@/offline/initializeOfflineCache'
import { initializeSpatialKeyBindings } from '@/tv/initializeSpatialKeyBindings'
import { scrollIntoViewOnFocus } from '@/util/domUtil'
import { initializeDeviceStyling } from '@/device/initializeDeviceStyling'
import { createDisposer, getUserAgentInfo, logDebug, logInfo } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { getDeviceConfig } from '@/device/deviceConfig'
import { doWhenIdle, IdlePriority } from '@/util/idleQueue'
import { pruneOptimisticUpdates } from '@amzn/amp-gql'
import { reapplyPersistedTrackLikes } from '@/track/trackLikeOverlay'
import { reapplyPersistedAlbumLikes } from '@/album/albumLikeOverlay'
import { reapplyPersistedArtistFollows } from '@/service/artistFollowOverlay'
import { createErrorHandler, initializeGlobalErrorHandling, onError } from '@/errorHandler'
import { setLastError } from '@/core/lastError'
import { debug } from '@/core/debug'
import { toastError } from '@/components/toast'
import { initializeRouter, registerRoutes } from '@/routes'
import { preloadRoute } from '@/router/router'
import { createAppDependencies } from '@/appDependencies'
import { initializeLogging } from '@/core/initializeLogging'
import { bullfrogVersion } from '@/version'
import { reloadApplication, setReloadApplicationHandler } from '@/core/reloadApplication'
import { initializeMediaKeyBindings } from '@/transport/mediaKeyBinding'
import { installGlobalKeyboardShortcuts } from '@/transport/keyboardShortcuts'
import { registerTransportSkipEffects } from '@/transport/skipGate'
import { installOtaUpdater } from '@/ota/installOtaUpdater'
import { initializeTauri } from '@/service/initializeTauri'
import { installPlaySearchBridgeLazy } from '@/voice/playSearchBridge'
import { initializeInactivityMonitor } from '@/messaging/inactivityMonitor'
import { initializeSleepTimer } from '@/playback/sleepTimerController'
import { applyTheme, resolveBootTheme, watchSystemTheme } from '@/blackbolt/settings/theme'
import { registerAgentWarmer } from '@/blackbolt/agent/agentWarmup'
import { hydrateAgentProviderPref } from '@/blackbolt/agent/agentProviderPref'
import { BEDROCK_PROVIDER_ID } from '@/blackbolt/agent/agentProviderIds'
import { installExplicitLanguageFilterSyncLazy } from '@/explicitLanguageFilter/installExplicitLanguageFilterSyncLazy'
import { clearPrerenderRoot } from '@amzn/prerender-contract'
import { staticPage } from '@/staticRender'
import { checkAndShowCookieConsentModalLazy } from '@/messaging/checkAndShowCookieConsentModal'
import { preseedCacheManager } from '@/service/preseedCacheManager'
import { initializeQueueRestoreLazy } from '@/cloudQueue/initializeQueueRestoreLazy'
import { getAuth } from '@/auth/authRef'
import { installSignOutTeardown } from '@/teardown'
import { queryParam } from '@/util/queryParams'

stratusConfig.value = {
    host: getApiHost(),
    region: 'NA',
}
cblAuthConfig.value = {
    host: proxiedHost('/proxy/api', 'https://api.amazon.com'),
}

const { target } = ctx('app')
const errorHandler = createErrorHandler(target)

;(async () => {
    // No Tauri runtime in a headless render; skip.
    if (!staticPage) await initializeTauri()
    onError(setLastError)
    if (debug) onError(({ error }) => toastError(asError(error)))
    initializeGlobalErrorHandling()
    initializeLogging()
    // `?v=<hash>` cache-busts the i18n bundles: their JSON ships via publicDir
    // (not fingerprinted), so a changed bundle needs a changed URL to defeat the
    // browser/CDN cache. The hash (baked by vite `define`) changes only when
    // translation content changes. The `{bundle}`/`{locale}` interpolation in
    // amp-i18n's loader leaves the query untouched; the static-prerender disk
    // resolver bypasses translationPath entirely, so this is browser-only.
    const i18nVersion = typeof __I18N_CONTENT_HASH__ !== 'undefined' ? __I18N_CONTENT_HASH__ : ''
    setI18nConfig({
        translationPath: `/i18n/{bundle}/{locale}.json${i18nVersion ? `?v=${i18nVersion}` : ''}`,
    })
    // Seed the UI locale: a saved display-language preference wins over the
    // browser locale (else nothing re-reads the choice and reload reverts it).
    initializeDisplayLocale()
    await initializeDeviceInfo()
    initializeDeviceStyling()
    applyTheme(resolveBootTheme())

    logInfo(target, `👋 Bullfrog ${bullfrogVersion.str}`)
    logDebug(target, 'initializing', getUserAgentInfo(), getDeviceConfig())

    // Prune stale persisted optimistic overlays once the app goes idle.
    doWhenIdle(() => pruneOptimisticUpdates(), IdlePriority.LOW).catch(errorHandler)

    // Re-arm the normalized cache from persisted track likes / album likes /
    // artist follows before the first entity query settles, so a change made
    // pre-reload out-ranks the gateway's stale echo within its window. Eager,
    // not idle-gated. Each overlay is a leaf (no mutation/toggle machinery) to
    // keep the boot chunk small.
    reapplyPersistedTrackLikes().catch(errorHandler)
    reapplyPersistedAlbumLikes().catch(errorHandler)
    reapplyPersistedArtistFollows().catch(errorHandler)

    // A render is network-free and single-shot; nothing to cache or purge.
    if (!staticPage) initializeOfflineCache()
    // Must precede the first data fetch (`reloadApplication` mounts the route below).
    await preseedCacheManager()
    initializeConnectedObserver()
    hideMainLoader()
    if (!staticPage) {
        scrollIntoViewOnFocus()
        // Sign-out is decoupled from teardown: `getAuth().signOut()` only drops
        // auth, and this listener runs the full cleanup on the signed-out
        // transition (app-root lifetime, never disposed).
        installSignOutTeardown(getAuth().status)
        // E2e-only: CBL boots signed out (its controller, unlike LWA, doesn't
        // read persistence on construct), so the harness triggers the silent
        // refresh-token sign-in via this param. Inert without it.
        if (queryParam('e2eSignIn') != null) getAuth().signIn()
        // Wire the library-owned Add to Playlist modal into the neutral opener
        // seam so track/podcast/album open it without importing `@/library`.
        // Dynamic import keeps the modal + its library/playlistdetail deps off
        // the boot chunk; the modal isn't needed until a user opens it.
        import('@/library/AddToPlaylistModal')
            .then(({ registerAddToPlaylistModal }) => registerAddToPlaylistModal())
            .catch(errorHandler)
        import('@/library/libraryService')
            .then(({ registerLibraryHomeFollowRefresh }) => registerLibraryHomeFollowRefresh())
            .catch(errorHandler)
        installOtaUpdater()
        // Inject the transport-domain skip-denial surface (tooltip + DM upsell)
        // into the playback-layer skip guard. Done once at boot so any guarded
        // gesture — including the track overflow menu's, which can't import
        // transport chrome without a biref cycle — paints the upsell on denial.
        registerTransportSkipEffects()
        // Disabled, can be added to prompt to install as a PWA
        // initializeInstallPrompt()
        // Seed the persisted chat-agent provider preference before reading
        // provider selection — the pref's async get() would otherwise race this
        // sync read and the choice wouldn't take effect until a later turn. The
        // get is a microtask over localStorage, so the await is cheap.
        await hydrateAgentProviderPref()
        // Inject the agent-connection warmer: the blackbolt tier (chat-input
        // focus, `ensureAgentConnected`) warms the selected provider through
        // this seam WITHOUT a `blackbolt → @/bedrockAgent` import (that back-edge
        // would close the biref cycle; see agentWarmup.ts). app.ts owns the
        // dynamic import — the chat provider is used later (on a turn), so its
        // registration (+ transport/idb chunk) needn't block boot. Fired only on
        // first chat-input focus, so a default (claw) boot never loads it.
        registerAgentWarmer((providerId) => {
            if (providerId === BEDROCK_PROVIDER_ID) {
                import('@/bedrockAgent')
                    .then(({ registerBedrockAgentProvider }) => registerBedrockAgentProvider())
                    .catch(errorHandler)
            }
        })
        // Boot-warm the top-level browse destinations on the idle queue.
        for (const path of ['/music', '/podcasts', '/audiobooks', '/my/library']) {
            preloadRoute(path, { idle: true })
        }
    }

    setReloadApplicationHandler(() => {
        const { add, dispose } = createDisposer()
        logDebug(target, 'reload application')

        if (!staticPage) add(watchSystemTheme())
        const deps = add(createAppDependencies()).dependencies

        clearPrerenderRoot()
        add(initializeAppView({ deps }))
        add(initializeRouter())
        add(registerRoutes(deps))
        if (!staticPage) {
            installPlaySearchBridgeLazy({ cloudQueueController: deps.cloudQueueController })
            add(initializePlaylistPlayerBindings(deps))
            // Recover the playing queue + now-playing track (PAUSED) on reload,
            // and persist the identifiers needed to do so as the queue changes.
            // rehydrateEntity is a best-effort by-id overlay injected lazily;
            // restore falls back to the queue node if it rejects.
            add(
                initializeQueueRestoreLazy({
                    playlistController: deps.playlistController,
                    cloudQueueController: deps.cloudQueueController,
                    playerPromise: deps.playerPromise,
                    rehydrateEntity: (entity) =>
                        import('@/track/restoreEntityRehydrate').then((m) =>
                            m.rehydrateRestoredEntity(entity)
                        ),
                })
            )
            add(initializeDynamicMessagingLazy(deps))
            add(initializeMediaSessionBindings(deps))
            add(initializeMediaKeyBindings(deps))
            add(
                installGlobalKeyboardShortcuts({
                    playerPromise: deps.playerPromise,
                    playlistController: deps.playlistController,
                })
            )
            add(initializeSpatialKeyBindings())
            add(initializeInactivityMonitor(deps))
            initializeSleepTimer(deps)
            add(installExplicitLanguageFilterSyncLazy())
            add(installDisplayLanguageSync())
            add(checkAndShowCookieConsentModalLazy())
        }

        return () => {
            logDebug(target, 'dispose')
            dispose()
        }
    })

    // Gate at app load: without a valid invite (or a prior sticky unlock) show
    // the wall instead of mounting a route. A non-expiring token in the PWA
    // start_url re-unlocks the iOS home-screen partition on its first launch.
    if (await checkInviteGate()) {
        reloadApplication()
    } else {
        document.body.append(InviteRequiredWall())
    }
})().catch(errorHandler)
